import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  fetchBookings,
  // clearBookings,
  fetchBookingsByResourceId,
} from '../../store/slices/bookingSlice';
import { AppDispatch } from '../../store';
import dayjs from 'dayjs';
import { useAuth } from '../auth/useAuth';

export const useBookings = () => {
  const startDate = dayjs().startOf('week').format('YYYY-MM-DD');
  const endDate = dayjs().endOf('week').format('YYYY-MM-DD');
  const dispatch = useDispatch<AppDispatch>();
  const {
    items: bookings,
    externalEvents,
    isLoading,
    error,
    lastFetched,
  } = useSelector((state: RootState) => state.bookings);
  const selectedOrganizationId = useSelector(
    (state: RootState) => state.organizationContext.selectedOrganizationId
  );
  const { role, clientResourceId } = useAuth();

  const initialFetchDone = useRef(false);

  const fetchData = useCallback(async () => {
    if (!selectedOrganizationId) return;

    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    if (!lastFetched || Date.now() - lastFetched > CACHE_DURATION) {
      if (role === 'admin') {
        // Fetch all bookings for the organization
        if (clientResourceId) {
          // Fetch only for the specific resource
          await dispatch(
            fetchBookingsByResourceId({
              organizationId: selectedOrganizationId,
              resourceId: clientResourceId,
              startDate,
              endDate,
            })
          );
        }
        await dispatch(
          fetchBookings({
            organizationId: selectedOrganizationId,
            startDate,
            endDate,
          })
        );
      } else if (clientResourceId) {
        // Fetch only for the specific resource
        await dispatch(
          fetchBookingsByResourceId({
            organizationId: selectedOrganizationId,
            resourceId: clientResourceId,
            startDate,
            endDate,
          })
        );
      }
    }
  }, [dispatch, selectedOrganizationId, lastFetched, role, clientResourceId]);

  useEffect(() => {
    if (!initialFetchDone.current || selectedOrganizationId) {
      fetchData();
      initialFetchDone.current = true;
    }
  }, [selectedOrganizationId]);

  const refetch = async (resourceId?: number, startDate?: string, endDate?: string) => {
    if (!selectedOrganizationId) return;

    const queryStartDate = startDate || dayjs().startOf('week').format('YYYY-MM-DD');
    const queryEndDate = endDate || dayjs().endOf('week').format('YYYY-MM-DD');

    if (role === 'admin') {
      if (resourceId) {
        await dispatch(
          fetchBookingsByResourceId({
            organizationId: selectedOrganizationId,
            resourceId: resourceId,
            startDate: queryStartDate,
            endDate: queryEndDate,
          })
        );
      } else {
        // Admin gets all bookings regardless of resource
        await dispatch(
          fetchBookings({
            organizationId: selectedOrganizationId,
            startDate: queryStartDate,
            endDate: queryEndDate,
          })
        );
      }
    } else if (resourceId) {
      await dispatch(
        fetchBookingsByResourceId({
          organizationId: selectedOrganizationId,
          resourceId: resourceId,
          startDate: queryStartDate,
          endDate: queryEndDate,
        })
      );
    }
  };

  return {
    bookings,
    externalEvents,
    isLoading,
    error,
    selectedOrganizationId,
    refetch,
  };
};
